// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";
import { enCA } from "date-fns/locale";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  Divider,
  CardMedia,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartTextInput, SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalTimePicker,
  ModalTimePickerSlotProps,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  strSymbol: {
    maxLength: 50,
    emptyAllowed: false,
    disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=]/,
  },
  nShift: {
    min: 0,
  },
  dStopLossForwardAdjustment: {
    digits: 1,
    step: 0.1,
  },
  dTakeProfitForwardAdjustment: {
    digits: 1,
    step: 0.1,
  },
};

// Chart patterns configuration
const CHART_PATTERNS = [
  {
    id: 0,
    displayName: "Bullish Wedge",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bullish_wedge.png",
    defaultEnabled: false,
  },
  {
    id: 1,
    displayName: "Bearish Wedge",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bearish_wedge.png",
    defaultEnabled: false,
  },
  {
    id: 2,
    displayName: "Bullish Trendchannel",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bullish_trend_channel.png",
    defaultEnabled: false,
  },
  {
    id: 3,
    displayName: "Bearish Trendchannel",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bearish_trend_channel.png",
    defaultEnabled: false,
  },
  {
    id: 4,
    displayName: "Bullish Triangle",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bullish_triangle.png",
    defaultEnabled: false,
  },
  {
    id: 5,
    displayName: "Bearish Triangle",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bearish_triangle.png",
    defaultEnabled: false,
  },
  {
    id: 6,
    displayName: "Shoulder Head Shoulder Top",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/shoulder_head_shoulder_top.png",
    defaultEnabled: false,
  },
  {
    id: 7,
    displayName: "Shoulder Head Shoulder Bottom",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/shoulder_head_shoulder_bottom.png",
    defaultEnabled: false,
  },
  {
    id: 8,
    displayName: "Double Top",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/double_top.png",
    defaultEnabled: false,
  },
  {
    id: 9,
    displayName: "Double Bottom",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/double_bottom.png",
    defaultEnabled: false,
  },
  {
    id: 10,
    displayName: "Triple Top",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/triple_top.png",
    defaultEnabled: false,
  },
  {
    id: 11,
    displayName: "Triple Bottom",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/triple_bottom.png",
    defaultEnabled: false,
  },
  {
    id: 12,
    displayName: "Rectangle Top",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/rectangle_top.png",
    defaultEnabled: false,
  },
  {
    id: 13,
    displayName: "Rectangle Bottom",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/rectangle_bottom.png",
    defaultEnabled: false,
  },
  {
    id: 14,
    displayName: "Bullish Symetric Triangle",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bullish_symetric_triangle.png",
    defaultEnabled: false,
  },
  {
    id: 15,
    displayName: "Bearish Symetric Triangle",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bearish_symetric_triangle.png",
    defaultEnabled: false,
  },
  {
    id: 16,
    displayName: "Bullish Gap",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bullish_gap.png",
    defaultEnabled: false,
  },
  {
    id: 17,
    displayName: "Bearish Gap",
    helpLink: "",
    image: "/illustrations/converted/chart_patterns/bearish_gap.png",
    defaultEnabled: false,
  },
];

// Timeframe options
const TIMEFRAME_OPTIONS = [
  { value: "-2", label: "Current" },
  { value: "0", label: "M1" },
  { value: "1", label: "M5" },
  { value: "2", label: "M15" },
  { value: "3", label: "M30" },
  { value: "4", label: "H1" },
  { value: "5", label: "H4" },
  { value: "6", label: "D1" },
  { value: "7", label: "W1" },
  { value: "8", label: "MN" },
];

// Reset values
export const ChartPatternsDefaults = {
  nSignalInUse: false,
  nSignalUsedFor: "0",
  strSymbol: "Current",
  nTimeframe: "-2",
  nShift: "0",
  nSignalVisualization: true,
  nDrawResults: true,
  nLogResults: false,
  strExclusion: "00:00:00-00:00:00",
  dStopLossForwardAdjustment: "1.00",
  dTakeProfitForwardAdjustment: "1.00",
  // Pattern enablement - use pattern-specific defaults
  ...Object.fromEntries(CHART_PATTERNS.map((pattern) => [`pattern_${pattern.id}`, pattern.defaultEnabled])),
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function ChartPatternsRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  const getSwitchDisplay = (value) => (value === true ? "On" : "Off");

  const getSignalUsedForDisplay = (value) => {
    const option = [
      { value: "0", label: "Entry" },
      { value: "1", label: "Exit" },
      { value: "2", label: "Forward" },
    ].find((opt) => opt.value === value);
    return option ? option.label : "Not set";
  };

  const getTimeframeDisplay = (value) => {
    const option = TIMEFRAME_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : "Not set";
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Signal in use", getSwitchDisplay(getDisplayValue("nSignalInUse")))}
      {renderParam("Signal used for", getSignalUsedForDisplay(getDisplayValue("nSignalUsedFor")))}

      <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

      {/* Available Patterns - Only show enabled ones */}
      {(() => {
        const enabledPatterns = CHART_PATTERNS.filter((pattern) => config[`pattern_${pattern.id}`] === true);
        if (enabledPatterns.length === 0) {
          return <Box key="no-patterns">{renderParam("Patterns", "No patterns enabled")}</Box>;
        }
        return enabledPatterns.map((pattern) => <Box key={pattern.id}>{renderParam(pattern.displayName, "On")}</Box>);
      })()}

      <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

      {renderParam("Symbol", getDisplayValue("strSymbol"))}
      {renderParam("Timeframe", getTimeframeDisplay(getDisplayValue("nTimeframe")))}
      {renderParam("Shift", getDisplayValue("nShift"))}

      <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

      {renderParam("Signal visualization", getSwitchDisplay(getDisplayValue("nSignalVisualization")))}
      {renderParam("Draw results", getSwitchDisplay(getDisplayValue("nDrawResults")))}
      {renderParam("Log results", getSwitchDisplay(getDisplayValue("nLogResults")))}
      {professionalMode === true && (
        <>
          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          {renderParam("Exclusion", getDisplayValue("strExclusion"))}
          {renderParam("Stop loss forward adjustment", getDisplayValue("dStopLossForwardAdjustment"))}
          {renderParam("Take profit forward adjustment", getDisplayValue("dTakeProfitForwardAdjustment"))}
        </>
      )}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const ChartPatternsConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  ChartPatternsConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Apply all the default values to the form
    Object.keys(ChartPatternsDefaults).forEach((key) => {
      setFieldValue(key, ChartPatternsDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Helper functions for exclusion time handling
  // ----------------------------------------------------------------------
  const parseExclusionString = (exclusionString) => {
    if (!exclusionString || exclusionString === "00:00:00-00:00:00") {
      return {
        start: dayjs("2000-01-01T00:00:00"),
        end: dayjs("2000-01-01T00:00:00"),
      };
    }

    const parts = exclusionString.split("-");
    if (parts.length === 2) {
      const [startTime, endTime] = parts;
      return {
        start: dayjs(`2000-01-01T${startTime}`),
        end: dayjs(`2000-01-01T${endTime}`),
      };
    }

    return {
      start: dayjs("2000-01-01T00:00:00"),
      end: dayjs("2000-01-01T00:00:00"),
    };
  };

  const formatExclusionString = (startTime, endTime) => {
    const start = startTime ? startTime.format("HH:mm:ss") : "00:00:00";
    const end = endTime ? endTime.format("HH:mm:ss") : "00:00:00";
    return `${start}-${end}`;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      //  parameters from the original file
      nSignalInUse: initialData?.nSignalInUse ?? ChartPatternsDefaults.nSignalInUse,
      nSignalUsedFor: initialData?.nSignalUsedFor ?? ChartPatternsDefaults.nSignalUsedFor,
      strSymbol: initialData?.strSymbol ?? ChartPatternsDefaults.strSymbol,
      nTimeframe: initialData?.nTimeframe ?? ChartPatternsDefaults.nTimeframe,
      nShift: initialData?.nShift ?? ChartPatternsDefaults.nShift,
      nSignalVisualization: initialData?.nSignalVisualization ?? ChartPatternsDefaults.nSignalVisualization,
      nDrawResults: initialData?.nDrawResults ?? ChartPatternsDefaults.nDrawResults,
      nLogResults: initialData?.nLogResults ?? ChartPatternsDefaults.nLogResults,
      strExclusion: initialData?.strExclusion ?? ChartPatternsDefaults.strExclusion,
      dStopLossForwardAdjustment: initialData?.dStopLossForwardAdjustment ?? ChartPatternsDefaults.dStopLossForwardAdjustment,
      dTakeProfitForwardAdjustment: initialData?.dTakeProfitForwardAdjustment ?? ChartPatternsDefaults.dTakeProfitForwardAdjustment,
      // Pattern enablement states
      ...Object.fromEntries(
        CHART_PATTERNS.map((pattern) => [
          `pattern_${pattern.id}`,
          initialData?.[`pattern_${pattern.id}`] ?? ChartPatternsDefaults[`pattern_${pattern.id}`],
        ])
      ),
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // Parse exclusion string for time pickers
  const exclusionTimes = parseExclusionString(values.strExclusion);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure chart patterns
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure chart patterns</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nSignalInUse */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <SmartParameter
                        value="Signal in use"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalInUse"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        checked={values.nSignalInUse === true}
                        onChange={(event) => {
                          setFieldValue("nSignalInUse", event.target.checked);
                        }}
                        sx={{ mt: "12px" }}
                      />
                    </Box>
                    <Box>
                      <SmartParameter
                        value="Signal used for"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalUsedFor"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nSignalUsedFor")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nSignalUsedFor && errors.nSignalUsedFor)}
                        >
                          <ModalContentMenuItem value="0">Entry</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Exit</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Forward</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Available Patterns Section */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {CHART_PATTERNS.map((pattern) => (
                      <Box key={pattern.id}>
                        <SmartParameter
                          value={pattern.displayName}
                          helpReference="HandleChartPatterns"
                          helpText=""
                          helpLink={pattern.helpLink}
                          fieldName={`pattern_${pattern.id}`}
                          touched={touched}
                          errors={errors}
                        />
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <ModalContentSwitch
                              checked={values[`pattern_${pattern.id}`] === true}
                              onChange={(event) => {
                                setFieldValue(`pattern_${pattern.id}`, event.target.checked);
                              }}
                              sx={{ mt: "6px", mb: "10px" }}
                            />
                            <Box
                              sx={{
                                borderRadius: "10px",
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "white",
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={pattern.image}
                                alt={pattern.displayName}
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: "177px",
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strSymbol */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <SmartParameter
                        value="Symbol"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="strSymbol"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartTextInput textFieldName="strSymbol" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                    <Box>
                      <SmartParameter
                        value="Timeframe"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="nTimeframe"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nTimeframe")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nTimeframe && errors.nTimeframe)}
                        >
                          {TIMEFRAME_OPTIONS.map((option) => (
                            <ModalContentMenuItem key={option.value} value={option.value}>
                              {option.label}
                            </ModalContentMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nShift */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <SmartParameter
                        value="Shift"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="nShift"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nShift" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nSignalVisualization */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <SmartParameter
                        value="Signal visualization"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalVisualization"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        checked={values.nSignalVisualization === true}
                        onChange={(event) => {
                          setFieldValue("nSignalVisualization", event.target.checked);
                        }}
                        sx={{ mt: "12px" }}
                      />
                    </Box>
                    <Box>
                      <SmartParameter
                        value="Draw results"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawResults"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        checked={values.nDrawResults === true}
                        onChange={(event) => {
                          setFieldValue("nDrawResults", event.target.checked);
                        }}
                        sx={{ mt: "12px" }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nLogResults */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <SmartParameter
                        value="Log results"
                        helpReference="HandleChartPatterns"
                        helpText=""
                        helpLink=""
                        fieldName="nLogResults"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        checked={values.nLogResults === true}
                        onChange={(event) => {
                          setFieldValue("nLogResults", event.target.checked);
                        }}
                        sx={{ mt: "12px" }}
                      />
                    </Box>
                  </Box>

                  {professionalMode === true && (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* strExclusion */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Excluded time range"
                          helpReference="HandleChartPatterns"
                          helpText=""
                          helpLink=""
                          fieldName="strExclusion"
                          touched={touched}
                          errors={errors}
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ModalTimePicker
                              value={exclusionTimes.start}
                              onChange={(value) => {
                                const newExclusionString = formatExclusionString(value, exclusionTimes.end);
                                setFieldValue("strExclusion", newExclusionString);
                              }}
                              ampm={false}
                              views={["hours", "minutes", "seconds"]}
                              slotProps={ModalTimePickerSlotProps}
                            />
                            <Typography variant="contentTiny">-</Typography>
                            <ModalTimePicker
                              value={exclusionTimes.end}
                              onChange={(value) => {
                                const newExclusionString = formatExclusionString(exclusionTimes.start, value);
                                setFieldValue("strExclusion", newExclusionString);
                              }}
                              ampm={false}
                              views={["hours", "minutes", "seconds"]}
                              slotProps={ModalTimePickerSlotProps}
                            />
                          </Box>
                        </LocalizationProvider>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* dStopLossForwardAdjustment */}
                      {/* ---------------------------------------------------------------------- */}
                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        <Box>
                          <SmartParameter
                            value="Stop loss forward adjustment"
                            helpReference="HandleChartPatterns"
                            helpText=""
                            helpLink=""
                            fieldName="dStopLossForwardAdjustment"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dStopLossForwardAdjustment" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                        <Box>
                          <SmartParameter
                            value="Take profit forward adjustment"
                            helpReference="HandleChartPatterns"
                            helpText=""
                            helpLink=""
                            fieldName="dTakeProfitForwardAdjustment"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dTakeProfitForwardAdjustment" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </Box>
                    </>
                  )}
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
